// O QUADRINHO GERADO SOBREVIVE À IDA AO LOGIN.
//
// ── O PROBLEMA QUE ISTO RESOLVE, E ELE NASCE DO PRÓPRIO CONSERTO ────────────────────────────
//
// `/roteirizar` é rota PÚBLICA por decisão registrada do operador: *"escrever uma história não
// precisa de conta; salvar precisa, e é aí que o login entra"*. Salvar passou a existir agora, e
// com ele o caminho "clicou em salvar, não tem conta, vai para o login".
//
// Só que esse caminho, feito do jeito ingênuo, é cruel: a criança escreveu a história, esperou os
// quadros serem desenhados um a um — dois, três minutos de espera olhando o progresso —, clicou
// em salvar, foi para o login, entrou... e voltou para uma tela em branco. O trabalho inteiro
// perdido exatamente por ter tentado salvá-lo.
//
// Então o documento vai junto para o login, e volta com ela.
//
// ── POR QUE `sessionStorage` E NÃO `localStorage` ───────────────────────────────────────────
//
// Duas razões, e a segunda é a que decide:
//
//   1. Isto é um rascunho de travessia, não memória. Ele existe para atravessar UMA ida ao login,
//      e some quando a aba fecha — que é exatamente a vida que ele deve ter.
//
//   2. **É conteúdo de criança num computador de escola, que é compartilhado.** `localStorage`
//      sobrevive ao fechamento do navegador: a história da aluna da manhã ficaria na máquina
//      esperando a aluna da tarde. `sessionStorage` morre com a aba.
//
// ── E O QUE FAZER QUANDO NÃO COUBER ─────────────────────────────────────────────────────────
//
// As imagens viajam em base64 dentro do documento, e um quadrinho de seis quadros passa
// facilmente dos poucos megabytes que o navegador dá por aba. Guardar o documento inteiro pode
// estourar a cota — e estourar a cota lança.
//
// Quando estoura, guardo o documento SEM as imagens e digo isso na volta. Perder os desenhos e
// manter a estrutura — o título, os quadros, as falas que ela escreveu — é muito melhor que
// perder tudo; e a estrutura é justamente a parte que ela não consegue refazer em dois cliques.
//
// ── E POR QUE O PARÂMETRO É `Storage | null`, E NÃO `| undefined` ───────────────────────────
//
// Valor padrão em JavaScript se aplica quando o argumento é `undefined`. Com a assinatura
// `Storage | undefined = sessionStorage`, passar `undefined` de propósito — que é como se diz
// "não há cofre" — ATIVA o padrão e devolve o `sessionStorage` de verdade. O caso "navegador sem
// armazenamento" ficaria sem como ser exercitado, e o teste dele passaria por engano.
//
// Com `null`, "nenhum cofre" é um valor que dá para passar.

import { DocumentoHQ } from "../../api/documento.types";

const CHAVE = "hq-lab:rascunho-do-roteirizador";

export interface RascunhoGuardado {
  documento: DocumentoHQ;
  /** As imagens não couberam e ficaram de fora. A tela avisa. */
  semImagens: boolean;
}

/** Tira o base64 de toda imagem, preservando o resto do documento. */
export function semAsImagens(documento: DocumentoHQ): DocumentoHQ {
  return {
    ...documento,
    paginas: (documento.paginas || []).map((pagina) => ({
      ...pagina,
      camadas: (pagina.camadas || []).map((camada) => ({
        ...camada,
        elementos: (camada.elementos || []).map((elemento) =>
          elemento.tipo === "imagem" ? { ...elemento, refImagem: "" } : elemento
        ),
      })),
    })),
  };
}

/**
 * Guarda o rascunho. Devolve o que de fato foi guardado, para a tela poder avisar.
 *
 * NUNCA LANÇA: quem chama está a caminho do login, e uma exceção aqui trocaria "você vai perder
 * os desenhos" por uma tela quebrada.
 */
export function guardarRascunho(
  documento: DocumentoHQ,
  cofre: Storage | null = typeof sessionStorage === "undefined" ? null : sessionStorage
): { guardou: boolean; semImagens: boolean } {
  if (!cofre) return { guardou: false, semImagens: false };

  try {
    cofre.setItem(CHAVE, JSON.stringify({ documento, semImagens: false }));
    return { guardou: true, semImagens: false };
  } catch {
    // Quase sempre cota estourada. Uma segunda tentativa, sem o que pesa.
  }

  try {
    cofre.setItem(CHAVE, JSON.stringify({ documento: semAsImagens(documento), semImagens: true }));
    return { guardou: true, semImagens: true };
  } catch {
    return { guardou: false, semImagens: false };
  }
}

/**
 * Recupera o rascunho e o APAGA na mesma chamada.
 *
 * Apagar na leitura é deliberado: sem isso, quem voltasse ao roteirizador pela terceira vez
 * receberia de volta a história de uma travessia antiga, por cima do que estivesse escrevendo.
 */
export function recuperarRascunho(
  cofre: Storage | null = typeof sessionStorage === "undefined" ? null : sessionStorage
): RascunhoGuardado | null {
  if (!cofre) return null;

  let bruto: string | null = null;
  try {
    bruto = cofre.getItem(CHAVE);
    cofre.removeItem(CHAVE);
  } catch {
    return null;
  }
  if (!bruto) return null;

  try {
    const lido = JSON.parse(bruto) as Partial<RascunhoGuardado>;
    // Documento sem página nenhuma não é rascunho: é lixo de uma versão antiga do formato, ou de
    // uma escrita interrompida. Devolvê-lo faria a tela mostrar um resultado vazio.
    if (!lido?.documento || !Array.isArray(lido.documento.paginas) || !lido.documento.paginas.length) {
      return null;
    }
    return { documento: lido.documento, semImagens: !!lido.semImagens };
  } catch {
    return null;
  }
}

/** Joga fora sem ler. Para quem começa uma história nova. */
export function descartarRascunho(
  cofre: Storage | null = typeof sessionStorage === "undefined" ? null : sessionStorage
): void {
  try {
    cofre?.removeItem(CHAVE);
  } catch {
    // Não conseguir limpar não é motivo para quebrar a página.
  }
}

// ═══════════════════════════════════════════════════════════════════════════════════════════
// O TEXTO TEM GAVETA PRÓPRIA — T9.4
//
// ── A TELA PROMETIA, E A PROMESSA ERA FALSA ─────────────────────────────────────────────────
//
// No alto da ferramenta está escrito, nestas palavras: *"O que você escrever não se perde: ele
// vai junto com você até o login e volta do outro lado."*
//
// Medido pelo QA em 02/10/2026, nos dois caminhos até o login:
//
//     pelo botão de guardar: desenhos que voltam ........ 2 de 2
//     pelo botão de guardar: letras do título ........... 0 de 16
//     pelo botão de guardar: letras da história ......... 0 de 62
//     pelo link "Entrar" do alto: o que é guardado ...... nada
//     caminhos testados / em que o texto volta ......... 2 / 0
//
// E a consequência não é só o susto: *"sem o texto ela não consegue corrigir uma palavra nem
// mandar desenhar de novo"*. Os desenhos voltavam e eram justamente a parte que ela não precisa
// reescrever; o texto, que é a parte dela, era o que sumia.
//
// A causa é de uma linha: o que atravessava era o DOCUMENTO, e o título e a história que a
// criança digitou são estado do formulário, que ninguém guardava.
//
// ── POR QUE UMA GAVETA SEPARADA, E NÃO UM CAMPO NO MESMO PACOTE ─────────────────────────────
//
// Porque elas têm problemas opostos. O documento carrega imagens em base64 e ESTOURA a cota —
// este arquivo tem um caminho de queda inteiro para isso, que joga as imagens fora para salvar a
// estrutura. O texto tem algumas centenas de bytes e nunca estoura nada.
//
// No mesmo pacote, o texto herdaria o risco do documento: uma cota estourada levaria os dois, e
// o que a criança menos pode perder é exatamente o mais leve de guardar. Separados, o texto cabe
// sempre.
//
// ── E POR ISSO ELE É GUARDADO AO SER DIGITADO, NÃO AO CLICAR EM SALVAR ──────────────────────
//
// O segundo caminho do achado é o link "Entrar" do alto da página, que não passa por botão
// nenhum desta tela. Guardar no clique conserta um caminho e deixa o outro — e a promessa fala
// de ir "até o login", não de ir por um botão específico. Texto que se guarda a cada tecla
// atravessa qualquer saída: o link do alto, o botão de voltar, uma aba recarregada.

const CHAVE_DO_TEXTO = "hq-lab:texto-do-roteirizador";

export interface TextoGuardado {
  titulo: string;
  texto: string;
  /**
   * T26.R6 · AS ESCOLHAS DO FORMULÁRIO VÃO JUNTO. O relatório de 09/10: «formato e categoria voltam
   * ao padrão ao sair e voltar da página, enquanto título e texto persistem. Gerei uma tirinha sem
   * querer e gastei uma rodada». Opcionais: uma gaveta antiga, só com título e texto, ainda abre.
   */
  formato?: string;
  categoria?: string;
  personagem?: string;
  /** T30.13 · e o estilo, que voltava a «Massinha 3D (o padrão)» no recarregar (relatório de 19/10). */
  estilo?: string;
}

/** Guarda o que a criança digitou. Nunca lança: é chamada a cada tecla. */
export function guardarOTexto(
  o: TextoGuardado,
  cofre: Storage | null = typeof sessionStorage === "undefined" ? null : sessionStorage
): boolean {
  if (!cofre) return false;
  // Nada digitado não vira gaveta vazia: a tela chama isto na primeira renderização, antes de a
  // volta do login ter chegado ao estado, e escrever vazio aqui apagaria o que acabou de voltar.
  if (!o.titulo && !o.texto) return false;
  try {
    cofre.setItem(
      CHAVE_DO_TEXTO,
      JSON.stringify({ titulo: o.titulo, texto: o.texto, formato: o.formato, categoria: o.categoria, personagem: o.personagem, estilo: o.estilo })
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Devolve o que foi digitado e APAGA na mesma chamada, pela mesma razão do rascunho do
 * documento: sem isso, a terceira entrada nesta tela traria de volta a história de uma
 * travessia antiga por cima do que a pessoa estivesse escrevendo.
 */
export function recuperarOTexto(
  cofre: Storage | null = typeof sessionStorage === "undefined" ? null : sessionStorage
): TextoGuardado | null {
  if (!cofre) return null;
  let bruto: string | null = null;
  try {
    bruto = cofre.getItem(CHAVE_DO_TEXTO);
    cofre.removeItem(CHAVE_DO_TEXTO);
  } catch {
    return null;
  }
  if (!bruto) return null;
  try {
    const lido = JSON.parse(bruto) as Partial<TextoGuardado>;
    const titulo = typeof lido?.titulo === "string" ? lido.titulo : "";
    const texto = typeof lido?.texto === "string" ? lido.texto : "";
    if (!titulo && !texto) return null;
    const opcional = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    return { titulo, texto, formato: opcional(lido.formato), categoria: opcional(lido.categoria), personagem: opcional(lido.personagem), estilo: opcional(lido.estilo) };
  } catch {
    return null;
  }
}

/** Joga fora sem ler. Para quem começa uma história nova. */
export function descartarOTexto(
  cofre: Storage | null = typeof sessionStorage === "undefined" ? null : sessionStorage
): void {
  try {
    cofre?.removeItem(CHAVE_DO_TEXTO);
  } catch {
    // Não conseguir limpar não é motivo para quebrar a página.
  }
}
